import { useEffect, useRef, useState } from 'react';
import useDataGridApi from "@/common/hooks/useDataGridApi.ts";
import { useSearchParamState } from "@/common/hooks/nuqs/useSearchParamState.ts";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";
import { EXPLORER_ROOT } from "@/features/Explorer/Explorer.tsx";

type ScrollPosition = Record<string, number>;

export function useDataGridScrollRestoration() {
  const [dataGridApi] = useDataGridApi();
  const storageKey = 'DATA_GRID_SCROLL_RESTORATION';
  const [hasRestoredScroll, setHasRestoredScroll] = useState(false);
  const previousPlaylistId = useRef<string | null>(null);
  const [playlistId] = useSearchParamState(URLSearchParamKeys.PLAYLIST, { defaultValue: EXPLORER_ROOT });
  const isLoading = dataGridApi?.current?.state.rows.loading;

  useEffect(() => {
    if (previousPlaylistId.current === null) {
      previousPlaylistId.current = playlistId;
      return;
    }

    if (previousPlaylistId.current !== playlistId) {
      setHasRestoredScroll(false);
      previousPlaylistId.current = playlistId;
    }
  }, [playlistId]);

  useEffect(() => {
    if (!dataGridApi?.current || isLoading || !playlistId || hasRestoredScroll) return;

    const restoreScrollIfNeeded = () => {
      const hasRows = (dataGridApi.current?.getRowsCount() || 0) > 0;
      if (!isLoading && hasRows && !hasRestoredScroll) {
        try {
          const savedData = localStorage.getItem(storageKey);
          if (!savedData) return;

          const scrollPositions: ScrollPosition = JSON.parse(savedData);
          const savedPosition = scrollPositions[playlistId];

          if (savedPosition !== undefined) {
            dataGridApi.current?.scroll({ top: savedPosition, left: 0 });
            setHasRestoredScroll(true);
          }
        } catch (error) {
          console.error('Error loading scroll state:', error);
        }
      }
    };

    const unsubscribe = dataGridApi.current.subscribeEvent('virtualScrollerContentSizeChange', restoreScrollIfNeeded);
    restoreScrollIfNeeded();

    return () => {
      unsubscribe();
    };
  }, [isLoading, dataGridApi, playlistId, storageKey, hasRestoredScroll]);

  useEffect(() => {
    if (!dataGridApi?.current || !playlistId) return;

    const handleScroll = () => {
      const scrollTop = dataGridApi?.current?.getScrollPosition().top || 0;

      try {
        const savedData = localStorage.getItem(storageKey);
        const scrollPositions: ScrollPosition = savedData ? JSON.parse(savedData) : {};

        scrollPositions[playlistId] = scrollTop;

        localStorage.setItem(storageKey, JSON.stringify(scrollPositions));
      } catch (error) {
        console.error('Error saving scroll state:', error);
      }
    };

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const debouncedScrollHandler = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(handleScroll, 200);
    };

    const unsubscribe = dataGridApi.current.subscribeEvent('scrollPositionChange', debouncedScrollHandler);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      unsubscribe();
    };
  }, [dataGridApi, playlistId, storageKey]);

  return {
    getInitialScrollState: () => {
      try {
        const savedData = localStorage.getItem(storageKey);
        const scrollPositions: ScrollPosition = savedData ? JSON.parse(savedData) : {};

        return {
          top: scrollPositions[playlistId!] || 0,
          left: 0
        };
      } catch (error) {
        console.error('Error retrieving scroll state:', error);
        return { top: 0, left: 0 };
      }
    }
  };
}
